import { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  ArrowLeft, User, Wallet, Gamepad2, History, Shield, DollarSign,
  Ban, CheckCircle, Activity, Users, Bell, Copy, RefreshCw, Plus,
  Minus, ArrowDownCircle, ArrowUpCircle, Clock, Star, Eye, BarChart3,
  Search, Link2
} from 'lucide-react';
import { fmtFP, fmtUSD } from '../../lib/money';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function PlayerDetailPage({ playerId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAdjust, setShowAdjust] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustType, setAdjustType] = useState('add');
  const [adjustTarget, setAdjustTarget] = useState('balance');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjusting, setAdjusting] = useState(false);
  const [availableGames, setAvailableGames] = useState([]);
  const [backendGameId, setBackendGameId] = useState('');
  const [backendQuery, setBackendQuery] = useState('');
  const [backendResults, setBackendResults] = useState([]);
  const [backendPasswords, setBackendPasswords] = useState({});
  const [backendSearching, setBackendSearching] = useState(false);
  const [backendSearched, setBackendSearched] = useState(false);
  const [backendLinkingKey, setBackendLinkingKey] = useState(null);

  useEffect(() => {
    fetchProfile();
    fetchAvailableGames();
  }, [playerId]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/admin/player-profile/${playerId}`);
      setData(res.data);
    } catch { toast.error('Failed to load player profile'); }
    finally { setLoading(false); }
  };

  const fetchAvailableGames = async () => {
    try {
      const res = await api.get('/api/admin/games');
      const items = res.data?.data?.items || res.data?.items || [];
      const enabled = items.filter(game => game.enabled !== false);
      setAvailableGames(enabled);
      setBackendGameId(prev => prev || enabled[0]?.id || enabled[0]?.code || '');
    } catch {
      setAvailableGames([]);
    }
  };

  const handleAdjust = async () => {
    if (!adjustAmount || !adjustReason) { toast.error('Amount and reason required'); return; }
    const parsedAmount = Number.parseFloat(adjustAmount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) { toast.error('Enter a valid amount'); return; }
    setAdjusting(true);
    try {
      const amt = parsedAmount * (adjustType === 'subtract' ? -1 : 1);
      await api.post(`/api/admin/users/${playerId}/adjust-wallet`, { amount: amt, adjust_type: adjustTarget, reason: adjustReason });
      const targetName = adjustTarget === 'play_credits' ? 'Freeplay balance' : 'Wallet balance';
      toast.success(`${targetName} ${adjustType === 'add' ? 'credited' : 'debited'}`);
      setShowAdjust(false); setAdjustAmount(''); setAdjustReason('');
      fetchProfile();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setAdjusting(false); }
  };

  const searchBackendAccounts = async () => {
    const q = backendQuery.trim();
    if (!backendGameId) {
      toast.error('Choose the game first');
      return;
    }
    if (q.length < 2) {
      toast.error('Enter the old backend username');
      return;
    }
    setBackendSearching(true);
    setBackendSearched(true);
    try {
      const res = await api.get(`/api/admin/users/${playerId}/provider-game-accounts/search`, {
        params: { game_id: backendGameId, q, limit: 25 },
      });
      setBackendResults(res.data?.accounts || []);
      setBackendPasswords({});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to search backend accounts');
      setBackendResults([]);
    } finally {
      setBackendSearching(false);
    }
  };

  const linkBackendAccount = async (account) => {
    const rowKey = account?.key || `${account?.provider_game_id}:${account?.provider_account_id || account?.username}`;
    if (!account?.username || !rowKey) return;
    const password = (account.password || backendPasswords[rowKey] || '').trim();
    if (!password) {
      toast.error('Enter the old account password before linking');
      return;
    }
    const label = `${account.game_name || account.game_code || 'game'} / ${account.username || account.provider_account_id}`;
    if (!window.confirm(`Link old backend account ${label} to this player?`)) return;
    setBackendLinkingKey(rowKey);
    try {
      await api.post(`/api/admin/users/${playerId}/link-provider-backend-account`, {
        game_id: backendGameId,
        provider_username: account.username,
        provider_account_id: account.provider_account_id,
        provider_password: password,
        note: `Linked from provider backend search: ${backendQuery.trim()}`,
      });
      toast.success('Old backend account linked');
      setBackendResults(prev => prev.map(row => (row.key || `${row.provider_game_id}:${row.provider_account_id || row.username}`) === rowKey ? { ...row, already_linked_to_player: true, can_link: false } : row));
      fetchProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link backend account');
    } finally {
      setBackendLinkingKey(null);
    }
  };

  const toggleDisable = async () => {
    try {
      await api.post(`/api/admin/users/${playerId}/status`, { disabled: !data.player.disabled });
      toast.success(data.player.disabled ? 'Player enabled' : 'Player disabled');
      fetchProfile();
    } catch { toast.error('Failed'); }
  };

  const forceLogout = async () => {
    try { await api.post(`/api/admin/users/${playerId}/force-logout`); toast.success('Sessions invalidated'); } catch { toast.error('Failed'); }
  };

  const copyText = (t) => { navigator.clipboard.writeText(t); toast.success('Copied!'); };
  const fmtInt = (v) => (v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  if (!data?.player) return <div className="text-center py-12 text-gray-500">Player not found</div>;

  const p = data.player;
  const w = data.wallet || {};
  const tabs = ['overview', 'games', 'transactions', 'activity', 'referrals'];
  const adjustTargets = [
    { value: 'balance', label: 'Wallet Cash', helper: 'Main wallet', current: fmtUSD(w.cash_balance), color: '#22c55e' },
    { value: 'play_credits', label: 'Freeplay FP', helper: 'Bonus credits', current: fmtFP(w.play_credits_balance || w.play_credits), color: '#3B82F6' },
  ];
  const activeAdjustTarget = adjustTargets.find(t => t.value === adjustTarget) || adjustTargets[0];
  const selectedBackendGame = availableGames.find(game => (game.id || game.code) === backendGameId) || availableGames[0];

  return (
    <div className="space-y-4" data-testid="player-detail-page">
      <button onClick={onBack} className="flex items-center gap-2 text-gray-400 hover:text-white text-sm" data-testid="back-to-players">
        <ArrowLeft className="w-4 h-4" /> Back to Players
      </button>

      {/* Player Header */}
      <div className="bg-[#131822] rounded-2xl border border-white/10 p-5 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-[#D4AF37]/15 flex items-center justify-center text-[#D4AF37] text-xl font-bold">
            {p.username?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">{p.username}</h2>
            <div className="flex items-center gap-1 mt-0.5">
              <p className="text-sm text-gray-400">{p.email}</p>
              <button onClick={() => copyText(p.email)} className="text-gray-600 hover:text-gray-400"><Copy className="w-3 h-3" /></button>
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <span className={`text-[9px] px-2 py-0.5 rounded-full font-semibold ${p.disabled ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                {p.disabled ? 'DISABLED' : 'ACTIVE'}
              </span>
              <span className="text-[9px] px-2 py-0.5 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] font-semibold capitalize">{p.achievement_tier || 'Bronze'}</span>
              {p.tenant_id && <span className="text-[9px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-400 font-medium">Tenant</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={forceLogout} className="border-white/10 text-xs"><Shield className="w-3 h-3 mr-1" /> Force Logout</Button>
          <Button size="sm" onClick={toggleDisable} className={`text-xs ${p.disabled ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}>
            {p.disabled ? <><CheckCircle className="w-3 h-3 mr-1" /> Enable</> : <><Ban className="w-3 h-3 mr-1" /> Disable</>}
          </Button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <StatCard label="Cash Balance" value={fmtUSD(w.cash_balance)} color="#22c55e" icon={Wallet} />
        <StatCard label="FP Credits" value={fmtFP(w.play_credits_balance || w.play_credits)} color="#3B82F6" icon={Star} />
        <StatCard label="Pending" value={fmtUSD(w.pending)} color="#eab308" icon={Clock} />
        <StatCard label="Game Accounts" value={data.game_accounts?.length || 0} color="#D4AF37" icon={Gamepad2} />
        <StatCard label="Referrals" value={data.referral_count || 0} color="#8b5cf6" icon={Users} />
        <StatCard label="Sessions" value={data.total_sessions || 0} color="#f97316" icon={Activity} />
      </div>

      {/* Adjust Balance */}
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => setShowAdjust(!showAdjust)} className="bg-[#D4AF37] text-black text-xs" data-testid="adjust-btn">
          <DollarSign className="w-3 h-3 mr-1" /> Adjust Balance / FP
        </Button>
        {data.game_summary?.load && <span className="text-xs text-gray-500">Loaded: {fmtUSD(data.game_summary.load.total)} ({data.game_summary.load.count} times)</span>}
        {data.game_summary?.redeem && <span className="text-xs text-gray-500 ml-2">Redeemed: {fmtUSD(data.game_summary.redeem.total)} ({data.game_summary.redeem.count} times)</span>}
      </div>
      {showAdjust && (
        <div className="bg-[#131822] rounded-xl border border-white/10 p-4 space-y-3">
          <div className="grid sm:grid-cols-2 gap-2">
            {adjustTargets.map(target => (
              <button
                key={target.value}
                type="button"
                onClick={() => setAdjustTarget(target.value)}
                className={`rounded-xl border px-3 py-2 text-left transition ${adjustTarget === target.value ? 'bg-white/10' : 'bg-[#0B0E14] hover:bg-white/5'}`}
                style={{ borderColor: adjustTarget === target.value ? target.color : 'rgba(255,255,255,0.10)' }}
                data-testid={`adjust-target-${target.value}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-white">{target.label}</span>
                  <span className="text-sm font-bold" style={{ color: target.color }}>{target.current}</span>
                </div>
                <p className="text-[10px] text-gray-500 mt-0.5">{target.helper}</p>
              </button>
            ))}
          </div>
          <div className="grid md:grid-cols-4 gap-3">
            <select value={adjustType} onChange={e => setAdjustType(e.target.value)} className="bg-[#0B0E14] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
              <option value="add">Add</option>
              <option value="subtract">Subtract</option>
            </select>
            <Input
              type="number"
              min="0"
              step={adjustTarget === 'play_credits' ? '1' : '0.01'}
              value={adjustAmount}
              onChange={e => setAdjustAmount(e.target.value)}
              placeholder={adjustTarget === 'play_credits' ? 'FP amount' : 'Dollar amount'}
              className="bg-[#0B0E14] border-white/10"
            />
            <Input value={adjustReason} onChange={e => setAdjustReason(e.target.value)} placeholder="Reason" className="bg-[#0B0E14] border-white/10" />
            <Button onClick={handleAdjust} disabled={adjusting} className="bg-[#D4AF37] text-black">{adjusting ? 'Processing...' : `Apply ${activeAdjustTarget.label}`}</Button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-[#131822] rounded-lg p-1">
        {tabs.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`flex-1 py-2 rounded-md text-xs font-medium capitalize transition ${activeTab === tab ? 'bg-[#D4AF37] text-black' : 'text-gray-400 hover:text-white'}`}
            data-testid={`tab-${tab}`}>
            {tab}
          </button>
        ))}
      </div>

      {/* Overview */}
      {activeTab === 'overview' && (
        <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
          <div className="grid md:grid-cols-2 gap-3">
            <InfoRow label="User ID" value={p.id} copyable />
            <InfoRow label="Username" value={p.username} />
            <InfoRow label="Email" value={p.email} copyable />
            <InfoRow label="Full Name" value={p.full_name || 'Not set'} />
            <InfoRow label="Phone" value={p.phone_number || p.phone_e164 || 'Not set'} />
            <InfoRow label="Referral Code" value={p.referral_code} copyable />
            <InfoRow label="Referred By" value={p.referred_by || p.staff_referral_code || 'None'} />
            <InfoRow label="VIP Tier" value={(p.achievement_tier || 'Bronze').toUpperCase()} />
            <InfoRow label="Total XP" value={fmtInt(p.total_xp_earned)} />
            <InfoRow label="Referral Count" value={p.referral_count || 0} />
            <InfoRow label="Tenant" value={p.tenant_id || 'Main Platform'} />
            <InfoRow label="Joined" value={p.created_at ? new Date(p.created_at).toLocaleString() : '—'} />
          </div>
          {/* Transaction Summary */}
          {Object.keys(data.transaction_summary || {}).length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/5">
              <h4 className="text-xs text-gray-500 font-medium mb-2">TRANSACTION SUMMARY</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.entries(data.transaction_summary).map(([key, val]) => (
                  <div key={key} className="bg-[#0B0E14] rounded-lg px-3 py-2">
                    <p className="text-[9px] text-gray-500 capitalize">{key.replace(/_/g, ' ')}</p>
                    <p className="text-sm font-semibold text-white">{fmtUSD(val.total)} <span className="text-gray-600">({val.count})</span></p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Games */}
      {activeTab === 'games' && (
        <div className="space-y-2">
          <div className="bg-[#131822] rounded-2xl border border-white/10 p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-[#D4AF37]" /> Link Old Provider Backend Account
                </h3>
                <p className="text-[11px] text-gray-500 mt-1">
                  Choose a game, search the upstream backend usernames, then attach the selected old account to this player.
                </p>
              </div>
              <span className="text-[9px] px-2 py-1 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] font-semibold">BACKEND SEARCH</span>
            </div>
            <div className="grid md:grid-cols-[220px_1fr_auto] gap-2">
              <select
                value={backendGameId}
                onChange={e => {
                  setBackendGameId(e.target.value);
                  setBackendResults([]);
                  setBackendSearched(false);
                }}
                className="bg-[#0B0E14] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                data-testid="provider-backend-game"
              >
                {availableGames.length === 0 && <option value="">No games loaded</option>}
                {availableGames.map(game => (
                  <option key={game.id || game.code} value={game.id || game.code}>
                    {game.name || game.code}
                  </option>
                ))}
              </select>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input
                  value={backendQuery}
                  onChange={e => setBackendQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && searchBackendAccounts()}
                  placeholder={`Search old ${selectedBackendGame?.name || 'game'} username...`}
                  className="pl-9 bg-[#0B0E14] border-white/10"
                  data-testid="provider-backend-search"
                />
              </div>
              <Button onClick={searchBackendAccounts} disabled={backendSearching} className="bg-[#D4AF37] text-black">
                {backendSearching ? <div className="spinner w-4 h-4" /> : 'Search Backend'}
              </Button>
            </div>
            {backendResults.length > 0 && (
              <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                {backendResults.map(account => {
                  const rowKey = account.key || `${account.provider_game_id}:${account.provider_account_id || account.username}`;
                  const disabledReason = account.already_linked_to_player
                    ? 'Already linked'
                    : !account.can_link
                      ? 'Cannot link'
                      : '';
                  return (
                    <div key={rowKey} className="rounded-xl border border-white/10 bg-[#0B0E14] p-3 space-y-3">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-sm font-semibold text-white">{account.game_name || account.game_code || 'Unknown game'}</p>
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-500/15 text-green-400">Found on backend</span>
                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${account.has_password ? 'bg-blue-500/15 text-blue-300' : 'bg-yellow-500/15 text-yellow-300'}`}>
                              {account.has_password ? 'Password included' : 'Password needed'}
                            </span>
                          </div>
                          <p className="text-[11px] text-gray-400 mt-1">
                            User: <span className="text-gray-200 font-mono">{account.username || 'N/A'}</span>
                            <span className="mx-1 text-gray-700">|</span>
                            ID: <span className="text-gray-200 font-mono">{account.provider_user_id || account.provider_account_id || 'N/A'}</span>
                          </p>
                          <p className="text-[10px] text-gray-600 mt-0.5">
                            Backend balance {fmtUSD(account.last_known_balance || 0)}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => linkBackendAccount(account)}
                          disabled={!account.can_link || backendLinkingKey === rowKey}
                          className={`${account.can_link ? 'bg-green-600 hover:bg-green-700' : 'bg-white/5 text-gray-500'} text-xs`}
                        >
                          {backendLinkingKey === rowKey ? <div className="spinner w-3 h-3" /> : (disabledReason || 'Link Account')}
                        </Button>
                      </div>
                      {!account.has_password && account.can_link && (
                        <Input
                          type="password"
                          value={backendPasswords[rowKey] || ''}
                          onChange={e => setBackendPasswords(prev => ({ ...prev, [rowKey]: e.target.value }))}
                          placeholder="Old account password"
                          className="bg-[#080B10] border-white/10"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {backendSearched && !backendSearching && backendResults.length === 0 && (
              <div className="rounded-xl border border-white/10 bg-[#0B0E14] px-3 py-4 text-center text-xs text-gray-500">
                No upstream backend accounts found for that game and username.
              </div>
            )}
          </div>
          {(data.game_accounts || []).length === 0 ? (
            <div className="text-center py-8 text-gray-500 bg-[#131822] rounded-xl border border-white/10"><Gamepad2 className="w-8 h-8 mx-auto mb-2 opacity-30" /><p className="text-sm">No game accounts</p></div>
          ) : data.game_accounts.map(acc => {
            const gameUsername = acc.username || acc.game_username || acc.provider_username || 'N/A';
            const providerId = acc.provider_account_id || acc.provider_user_id || 'N/A';
            const syncedAt = acc.last_balance_synced_at || acc.last_synced_at;
            const isLinked = acc.created_via === 'admin_store_link' || acc.created_via === 'admin_backend_link' || acc.created_via === 'admin_linked';
            return (
              <div key={acc.id} className="bg-[#131822] rounded-xl border border-white/10 p-4 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-white">{acc.game_name || acc.game_code}</p>
                    {isLinked && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-500/15 text-green-400">Linked old backend</span>}
                  </div>
                  <p className="text-[10px] text-gray-500">User: {gameUsername} | ID: {providerId} | Code: {acc.game_code || 'N/A'}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-sm font-bold text-[#D4AF37]">{fmtUSD(acc.last_known_balance || 0)}</p>
                    <p className="text-[9px] text-gray-600">{syncedAt ? `Synced: ${new Date(syncedAt).toLocaleTimeString()}` : 'Not synced'}</p>
                  </div>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${acc.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>{acc.status || 'active'}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Transactions */}
      {activeTab === 'transactions' && (
        <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
          <h3 className="text-sm font-semibold text-gray-400 mb-3 flex items-center gap-2"><History className="w-4 h-4 text-[#D4AF37]" /> Recent Transactions</h3>
          {(data.recent_transactions || []).length === 0 ? (
            <p className="text-center py-6 text-gray-500 text-sm">No transactions</p>
          ) : (
            <div className="space-y-1.5">
              {data.recent_transactions.map(txn => (
                <div key={txn.id} className="flex items-center justify-between bg-[#0B0E14] rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: txn.type === 'deposit' ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)' }}>
                      {txn.type === 'deposit' ? <ArrowDownCircle className="w-3.5 h-3.5 text-green-400" /> : <ArrowUpCircle className="w-3.5 h-3.5 text-red-400" />}
                    </div>
                    <div>
                      <p className="text-xs text-white capitalize">{txn.type}</p>
                      <p className="text-[9px] text-gray-600">{txn.method || ''} {txn.created_at?.slice(0, 16)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-semibold" style={{ color: txn.type === 'deposit' ? '#22c55e' : '#ef4444' }}>{fmtUSD(txn.amount)}</p>
                    <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${txn.status === 'approved' || txn.status === 'completed' ? 'bg-green-500/20 text-green-400' : txn.status === 'UNDER_REVIEW' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-500/20 text-gray-400'}`}>{txn.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Activity */}
      {activeTab === 'activity' && (
        <div className="space-y-4">
          {/* Mini heatmap */}
          {data.activity_heatmap?.max_value > 0 && (
            <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
              <h3 className="text-sm font-semibold text-gray-400 mb-3">Activity Heatmap (30 days)</h3>
              <div className="min-w-[500px]">
                {DAY_LABELS.map((day, di) => (
                  <div key={di} className="flex items-center mb-0.5">
                    <span className="w-8 text-[8px] text-gray-600 text-right pr-1">{day}</span>
                    <div className="flex flex-1 gap-px">
                      {(data.activity_heatmap.grid?.[di] || Array(24).fill(0)).map((val, hi) => {
                        const max = data.activity_heatmap.max_value || 1;
                        const intensity = val / max;
                        const bg = val === 0 ? 'rgba(255,255,255,0.03)' : `rgba(212,175,55,${0.15 + intensity * 0.85})`;
                        return <div key={hi} className="flex-1 rounded-sm" style={{ background: bg, height: 14 }} title={`${day} ${hi}:00 — ${val} events`} />;
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Recent Activity */}
          <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
            <h3 className="text-sm font-semibold text-gray-400 mb-3 flex items-center gap-2"><Activity className="w-4 h-4 text-[#D4AF37]" /> Recent Actions</h3>
            {(data.recent_activity || []).length === 0 ? (
              <p className="text-center py-6 text-gray-500 text-sm">No activity recorded</p>
            ) : (
              <div className="space-y-1.5">
                {data.recent_activity.map((act, i) => (
                  <div key={i} className="flex items-center justify-between bg-[#0B0E14] rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2.5">
                      <div className="w-6 h-6 rounded bg-[#D4AF37]/10 flex items-center justify-center">
                        <Activity className="w-3 h-3 text-[#D4AF37]" />
                      </div>
                      <span className="text-xs text-white capitalize">{act.action?.replace('_', ' ')}</span>
                    </div>
                    <span className="text-[10px] text-gray-600">{act.created_at?.slice(0, 16)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Referrals */}
      {activeTab === 'referrals' && (
        <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
          <h3 className="text-sm font-semibold text-gray-400 mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-[#8b5cf6]" /> Referred Players ({data.referral_count || 0})</h3>
          {(data.referrals || []).length === 0 ? (
            <p className="text-center py-6 text-gray-500 text-sm">No referrals</p>
          ) : (
            <div className="space-y-1.5">
              {data.referrals.map(r => (
                <div key={r.id} className="flex items-center justify-between bg-[#0B0E14] rounded-lg px-3 py-2">
                  <div>
                    <p className="text-sm text-white">{r.username}</p>
                    <p className="text-[10px] text-gray-500">{r.email}</p>
                  </div>
                  <span className="text-[10px] text-gray-600">{r.created_at?.slice(0, 10)}</span>
                </div>
              ))}
            </div>
          )}
          {/* Notifications */}
          {(data.notifications || []).length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/5">
              <h4 className="text-xs text-gray-500 font-medium mb-2 flex items-center gap-1.5"><Bell className="w-3 h-3" /> Recent Notifications</h4>
              <div className="space-y-1">
                {data.notifications.map((n, i) => (
                  <div key={i} className="flex items-center justify-between bg-[#0B0E14] rounded-lg px-3 py-2">
                    <div>
                      <p className="text-xs text-white">{n.title}</p>
                      <p className="text-[10px] text-gray-500">{n.message?.slice(0, 60)}</p>
                    </div>
                    <span className="text-[10px] text-gray-600">{n.created_at?.slice(0, 10)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div className="bg-[#131822] rounded-xl border border-white/10 p-3">
      <div className="flex items-center gap-2 mb-1"><Icon className="w-3.5 h-3.5" style={{ color }} /><p className="text-[9px] text-gray-500">{label}</p></div>
      <p className="text-base font-bold" style={{ color }}>{value}</p>
    </div>
  );
}

function InfoRow({ label, value, copyable }) {
  const copy = () => { if (value) { navigator.clipboard.writeText(String(value)); toast.success('Copied!'); } };
  return (
    <div className="flex items-center justify-between bg-[#0B0E14] rounded-lg px-3 py-2">
      <span className="text-[10px] text-gray-500">{label}</span>
      <div className="flex items-center gap-1">
        <span className="text-xs text-white font-mono truncate max-w-[200px]">{value}</span>
        {copyable && value && <button onClick={copy} className="text-gray-600 hover:text-gray-400"><Copy className="w-2.5 h-2.5" /></button>}
      </div>
    </div>
  );
}
